/* =====================================================================
   AstaLab 2.0 — ArtifactDemoBackendAdapter

   Implementazione locale/mock di BackendAdapter, usata SOLO quando non
   esiste una configurazione di rete valida (vedi backend/bootstrap.js) —
   in pratica sempre nell'Artifact Claude, il cui sandbox blocca ogni
   fetch/XHR/WebSocket verso un host esterno per costruzione della
   piattaforma (non per assenza di credenziali: nessuna chiave, per
   quanto reale, permetterebbe comunque una connessione di rete lì).

   REGOLE CHE QUESTO FILE NON VIOLA MAI (vedi anche ui/components/DemoModeBanner.js):
   - non dichiara mai un account "registrato su Supabase";
   - non dichiara mai che una password e' stata realmente inviata via email;
   - non dichiara mai un'asta "sincronizzata sul cloud";
   - non dichiara mai utenti remoti "realmente connessi";
   - non persiste MAI la password originale, nemmeno in chiaro in
     localStorage (revisione r1, obiettivo 4 — vedi hashPassword() sotto).
   Ogni risultato che simula un'operazione altrimenti di rete porta un
   campo `notice` testuale, onesto, che le pagine mostrano cosi' com'e'
   (mai un `if (backend.kind==="artifact-demo")` sparso nella business
   logic delle pagine — vedi BackendAdapter.js).

   CORREZIONE r1 (obiettivo 4): la alpha.1 salvava `{email, password}` in
   chiaro dentro l'oggetto serializzato in localStorage — un audit
   indipendente lo ha segnalato come falso rispetto alla dichiarazione
   "nessuna password in localStorage" del rapporto alpha.1 (quella frase
   si riferiva soltanto al CAMPO del form HTML, mai persistito come tale,
   ma non era vera per lo STORE demo, che è comunque dentro localStorage).
   Ora si persiste solo `{passwordSalt, passwordHash, passwordIterations}`
   — derivati con PBKDF2-SHA256 (WebCrypto, `crypto.subtle`, disponibile
   sia nel browser sia in Node ≥19 per i test) con un salt casuale per
   utente. Questa resta comunque una demo locale, mai pensata per
   custodire credenziali reali: da qui il messaggio esplicito richiesto
   "Modalità demo: non usare una password reale." (vedi RegisterPage.js e
   il campo `notice` di signUp()).
   ===================================================================== */
import { BackendAdapter, LocalAuthAttemptTracker } from "./BackendAdapter.js";
import { validatePasswordPolicy } from "../config/authPolicy.js";
import { canonicalizeJoinCode } from "../engine/joinCode.js";
import { logClientError } from "../utils/clientLogger.js";

const STORAGE_KEY = "astalab_2_demo_backend_v1";
const PBKDF2_ITERATIONS = 100000;
const SALT_BYTES = 16;

function uid(prefix){ return prefix + "_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36); }
function nowISO(){ return new Date().toISOString(); }

// DEMO PARITY (round alpha.5-r4-r3, BLOCKER B, seconda audit indipendente): p_command_id è tipizzato
// `uuid` lato SQL (0014) — BackendAdapter dichiara altrettanto esplicitamente commandId come UUID nel
// proprio contratto. Il Demo Adapter deve rifiutare una stringa non-UUID (es. "cmd-b") esattamente
// come lo rifiuterebbe una connessione reale (errore di tipo Postgres). Il contratto richiede
// semplicemente un UUID sintatticamente valido (qualunque versione RFC4122) — mai ristretto alla sola
// v4: la UI genera v4, ma il dominio applicativo/SQL non lo impone.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isValidUuid(v){ return typeof v === "string" && UUID_RE.test(v); }

function toHex(buffer){
  return Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, "0")).join("");
}
function fromHex(hex){
  const bytes = new Uint8Array(hex.length / 2);
  for(let i=0; i<bytes.length; i++) bytes[i] = parseInt(hex.substr(i*2, 2), 16);
  return bytes;
}
function subtleCrypto(){
  const c = (typeof globalThis !== "undefined") ? globalThis.crypto : undefined;
  if(!c || !c.subtle) throw new Error("WebCrypto (crypto.subtle) non disponibile in questo ambiente.");
  return c;
}

/** Deriva un hash PBKDF2-SHA256 da una password + salt — mai la password stessa viene restituita o conservata. */
async function derivePasswordHash(password, saltBytes, iterations){
  const c = subtleCrypto();
  const enc = new TextEncoder();
  const keyMaterial = await c.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveBits"]);
  const bits = await c.subtle.deriveBits(
    { name:"PBKDF2", salt: saltBytes, iterations, hash:"SHA-256" },
    keyMaterial, 256
  );
  return toHex(bits);
}
async function hashNewPassword(password){
  const c = subtleCrypto();
  const salt = c.getRandomValues(new Uint8Array(SALT_BYTES));
  const hash = await derivePasswordHash(password, salt, PBKDF2_ITERATIONS);
  return { passwordSalt: toHex(salt), passwordHash: hash, passwordIterations: PBKDF2_ITERATIONS };
}
async function verifyPassword(password, record){
  if(!record || !record.passwordSalt || !record.passwordHash) return false;
  const salt = fromHex(record.passwordSalt);
  const hash = await derivePasswordHash(password, salt, record.passwordIterations || PBKDF2_ITERATIONS);
  return hash === record.passwordHash;
}

// ---------------- Lobby multiplayer / join-code (round alpha.4-r1, corretto in alpha.4-r2) ----------------
// Stesso alfabeto/stesso formato del lato reale (private.generate_join_code_plain(),
// 0012_multiplayer_lobby_foundation.sql, BLOCKER B) — non un requisito tecnico (le due generazioni
// sono indipendenti, mai confrontate fra loro), ma tenerle allineate evita che un candidato di test
// "sembri" diverso fra i due adapter senza motivo. Il codice è a 12 caratteri/60 bit di entropia
// (era 10/50 bit in r1, allineato all'aumento richiesto dall'audit anche lato SQL). L'entropia qui
// era GIÀ crittografica in r1 (crypto.getRandomValues(), Web Crypto API — un vero CSPRNG, a
// differenza di random() lato SQL, che r1 usava e che il BLOCKER B ha corretto) — questo file non
// aveva quindi il difetto del BLOCKER B, solo la lunghezza/formato sono stati allineati per
// coerenza. L'hash usa SHA-256/WebCrypto, non md5: nessun vincolo di search_path/schema-qualification
// esiste nel browser (vedi MULTIPLAYER_SESSION_DESIGN_alpha4_r1.md §5) — usare l'algoritmo migliore
// disponibile qui è corretto perché nessun test confronta mai un hash calcolato lato demo con uno
// calcolato lato SQL.
const JOIN_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const JOIN_CODE_LENGTH = 12;
function generateJoinCodePlain(){
  const c = subtleCrypto();
  const bytes = c.getRandomValues(new Uint8Array(JOIN_CODE_LENGTH));
  let out = "";
  for(let i=0;i<JOIN_CODE_LENGTH;i++) out += JOIN_CODE_ALPHABET[bytes[i] % JOIN_CODE_ALPHABET.length];
  return out.slice(0,4) + "-" + out.slice(4,8) + "-" + out.slice(8);
}
async function hashJoinCodeCanonical(canonical){
  const c = subtleCrypto();
  const enc = new TextEncoder();
  const digest = await c.subtle.digest("SHA-256", enc.encode(canonical));
  return toHex(digest);
}
/** Una riga di membership sempre coerente con il vecchio formato "solo owner" (pre-alpha.4) di
 *  un'asta demo eventualmente persistita in un localStorage più vecchio — mai un throw su uno
 *  store legacy, sempre un fallback owner-only equivalente a quello che c'era prima. */
function membersOf(a){
  if(Array.isArray(a.members)) return a.members;
  return [{ userId:a.ownerId, role:"owner", joinedAt:a.createdAt }];
}

/** BLOCKER A (audit indipendente su r1): UNICO punto da cui QUALUNQUE read path decide se l'utente
 *  CORRENTE può vedere una data asta — mai una seconda implementazione divergente, mai un fallback
 *  che inventi un ruolo. Restituisce la riga di membership reale (mai un valore sintetico) o `null`
 *  se l'utente non è né owner né member — parità con `auctions_select_owner_or_member`/
 *  `auction_states_select_related` (0003, invariate) lato reale: `null` significa "non visibile",
 *  esattamente come una riga che RLS filtrerebbe via silenziosamente per un chiamante non
 *  autorizzato (mai un errore diverso da "non trovato" per un outsider — stesso comportamento onesto
 *  di un 404 reale, mai un dettaglio che riveli l'esistenza dell'asta a chi non vi appartiene). */
function membershipOf(a, userId){
  if(!a || !userId) return null;
  return membersOf(a).find(m => m.userId === userId) || null;
}

// ---------------- Live auction: seat/squadra + lifecycle (round alpha.5-r1) ----------------
// Stessa parità/stesso principio di membersOf()/membershipOf() sopra — un solo punto da cui
// qualunque metodo legge/inizializza queste due strutture, mai una seconda variante divergente. Vedi
// LIVE_AUCTION_DESIGN_alpha5_r1.md §1-§4: seat = squadra fantasy/partecipante economico, distinta
// dall'identità auction_members (MEMBER != TEAM); sessione live = state machine server-authoritative
// separata (qui: separata dal resto dell'oggetto asta, mai dentro auctionStates).
function seatsOf(a){
  if(Array.isArray(a.seats)) return a.seats;
  return [];
}
function liveSessionOf(a){
  return a.liveSession || null;
}

// ---------------- Live auction: nomina/offerta/timer (round alpha.5-r4-r1) ----------------
// Stesso principio di seatsOf()/liveSessionOf() sopra — un solo punto da cui qualunque metodo legge/
// inizializza queste strutture. auction_live_rules/auction_nominations/auction_bids (0014) vivono qui
// come liveRules/nominations/bids sull'oggetto asta, mai dentro auctionStates (stessa separazione già
// applicata a seats/liveSession).
function liveRulesOf(a){
  return a.liveRules || null;
}
function nominationsOf(a){
  if(Array.isArray(a.nominations)) return a.nominations;
  return [];
}
function bidsOf(a){
  if(Array.isArray(a.bids)) return a.bids;
  return [];
}

// ---------------- Live auction: settlement / purchase ledger / budget / roster (round alpha.5-r5-r1) ----------------
// Stesso principio di liveRulesOf()/nominationsOf()/bidsOf() sopra — un solo punto da cui qualunque
// metodo legge/inizializza queste strutture. rosterRules/purchases vivono qui direttamente
// sull'oggetto asta (mai dentro auctionStates, mai un campo mutabile di budget/roster residuo —
// auction_purchases è l'UNICA authority economica, sempre derivata per somma/conteggio).
function rosterRulesOf(a){
  return a.rosterRules || null;
}
function purchasesOf(a){
  if(Array.isArray(a.purchases)) return a.purchases;
  return [];
}

// Il catalogo Fantacalcio (players/ruolo/current) è un dato GLOBALE AstaLab, gestito da
// ArtifactDemoCatalogRepository sotto una chiave localStorage COMPLETAMENTE separata da questo
// adapter (mai duplicato/copiato dentro lo store delle aste, stesso principio "dati globali vs dati
// dell'asta" di ARCHITECTURE_2.md) — ma nominatePlayer() deve comunque poter risolvere ruolo/
// esistenza di un giocatore per validare una nomina, esattamente come la RPC reale legge
// `public.players` (anch'essa una tabella globale separata, mai posseduta dalle RPC seat/lifecycle).
// Questa è una LETTURA transitoria al momento della decisione, mai una copia persistita qui: nessuna
// scrittura avviene mai su questa chiave da questo file.
const CATALOG_STORAGE_KEY = "astalab_2_demo_catalog_v1";
function readCatalogPlayerRole(fantacalcioId){
  try{
    const raw = (typeof localStorage !== "undefined") ? localStorage.getItem(CATALOG_STORAGE_KEY) : null;
    if(!raw) return null;
    const state = JSON.parse(raw);
    const players = Array.isArray(state.players) ? state.players : [];
    const p = players.find(x => x.fantacalcioId === fantacalcioId && x.current === true);
    return p ? (p.ruolo || null) : null;
  }catch(e){ return null; }
}

function loadStore(){
  try{
    const raw = (typeof localStorage !== "undefined") ? localStorage.getItem(STORAGE_KEY) : null;
    if(!raw) return freshStore();
    const parsed = JSON.parse(raw);
    return Object.assign(freshStore(), parsed);
  }catch(e){
    return freshStore();
  }
}
function freshStore(){
  return { users: [], profiles: {}, sessionUserId: null, auctions: [], auctionStates: {} };
}
function saveStore(store){
  try{
    if(typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  }catch(e){ /* storage pieno/non disponibile: la sessione demo resta solo in memoria per questo giro */ }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEMO_PASSWORD_WARNING = "Modalità demo: non usare una password reale.";

export class ArtifactDemoBackendAdapter extends BackendAdapter{
  /**
   * @param {{clock?:()=>Date}} [opts] - round alpha.5-r4-r1: `clock` opzionale, iniettabile SOLO dai
   *   test (mai dalla UI, che non passa mai questo parametro) per rendere deterministica la deadline
   *   di nomina/offerta senza un vero sleep — di default `() => new Date()`. NESSUN parametro `store`:
   *   `this._store = loadStore()` resta sempre interno, ogni altro campo del costruttore (nessuno,
   *   in questo round) non deve mai rompere gli oltre 80 call-site esistenti `new
   *   ArtifactDemoBackendAdapter()` a zero argomenti — l'intero oggetto opts è opzionale.
   */
  constructor({ clock } = {}){
    super();
    this._store = loadStore();
    this._authListeners = new Set();
    this._clock = clock || (() => new Date());
    // Round Beta-P3-3 (design D4): per-istanza, mai persistito — vedi LocalAuthAttemptTracker.
    this._localAttempts = new LocalAuthAttemptTracker();
  }

  get kind(){ return "artifact-demo"; }

  _persist(){ saveStore(this._store); }
  _currentUserRecord(){
    if(!this._store.sessionUserId) return null;
    return this._store.users.find(u => u.id === this._store.sessionUserId) || null;
  }
  _publicUser(u){ return u ? { id: u.id, email: u.email } : null; }
  /** @param {"SIGNED_IN"|"SIGNED_OUT"|"USER_UPDATED"} event */
  _notifyAuthChange(event){
    const user = this._publicUser(this._currentUserRecord());
    // Round Beta-P3-3 (design D4): calcolato una sola volta per evento, dallo stesso identikit
    // riportato dall'evento stesso — mai un secondo canale di identità.
    const meta = { isLocalInteractiveAuthInFlight: !!(user && this._localAttempts.isInFlight(user.email)) };
    // Round Beta-P3-5 (design D2/D3, accettato): semantica di dispatch INVARIATA — un listener che
    // lancia non deve mai bloccare i successivi né propagarsi nel flusso auth; l'unica aggiunta è
    // un log diagnostico una tantum per QUESTO listener prima di continuare, mai un rethrow.
    this._authListeners.forEach(cb => {
      try{ cb(event, user, meta); }
      catch(e){ logClientError({ area:"auth", operation:"notify-listener", error:e }); }
    });
  }

  // Round Beta-P3-3 (design D4): this._localAttempts.begin(email) è la PRIMISSIMA istruzione —
  // prima di qualunque validazione/await/possibile evento SIGNED_IN — e .end(email) vive in un
  // finally che avvolge l'INTERO corpo, cosi' il contatore si rilascia su OGNI percorso di uscita
  // (validazione fallita, fallimento normale, successo, eccezione inattesa). L'email grezza in
  // ingresso viene normalizzata internamente dal tracker — nessuna divergenza possibile con la
  // normalizzazione locale (email = ...) poco sotto, che resta invariata.
  async signUp(email, password){
    this._localAttempts.begin(email);
    try{
      email = String(email || "").trim().toLowerCase();
      if(!EMAIL_RE.test(email)) return { ok:false, user:null, error:"Indirizzo email non valido.", notice:null };
      const policy = validatePasswordPolicy(password);
      if(!policy.ok) return { ok:false, user:null, error:policy.error, notice:null };
      if(this._store.users.some(u => u.email === email)){
        return { ok:false, user:null, error:"Esiste già un account demo con questa email.", notice:null };
      }
      const credentials = await hashNewPassword(password);
      const user = Object.assign({ id: uid("demouser"), email, createdAt: nowISO() }, credentials);
      this._store.users.push(user);
      this._store.profiles[user.id] = { id: user.id, displayName: email.split("@")[0], createdAt: nowISO() };
      this._store.sessionUserId = user.id;
      this._persist();
      this._notifyAuthChange("SIGNED_IN");
      return {
        ok:true, user: this._publicUser(user), error:null,
        notice:"Modalità demo Artifact: nessun account è stato realmente registrato su Supabase — questi dati esistono solo in questo browser. " + DEMO_PASSWORD_WARNING
      };
    } finally {
      this._localAttempts.end(email);
    }
  }

  async signIn(email, password){
    this._localAttempts.begin(email);
    try{
      email = String(email || "").trim().toLowerCase();
      const user = this._store.users.find(u => u.email === email);
      const valid = user ? await verifyPassword(password, user) : false;
      if(!user || !valid) return { ok:false, user:null, error:"Email o password demo non corrette.", notice:null };
      this._store.sessionUserId = user.id;
      this._persist();
      this._notifyAuthChange("SIGNED_IN");
      return { ok:true, user: this._publicUser(user), error:null, notice:null };
    } finally {
      this._localAttempts.end(email);
    }
  }

  async signOut(){
    this._store.sessionUserId = null;
    this._persist();
    this._notifyAuthChange("SIGNED_OUT");
    return { ok:true, error:null };
  }

  async getCurrentUser(){
    return this._publicUser(this._currentUserRecord());
  }

  async requestPasswordReset(email){
    email = String(email || "").trim().toLowerCase();
    // Risposta identica esista o meno l'account (stessa cautela della 1.3/di un vero flusso auth:
    // non rivelare quali email sono registrate), ma con un notice sempre onesto sul fatto che non
    // e' stata inviata alcuna email reale. La demo non ha un vero link di recupero: la pagina
    // /reset-password resta comunque raggiungibile direttamente (vedi ResetPasswordPage.js),
    // coerente con "operazioni demo possono usare storage locale per provare integralmente la UX".
    return {
      ok:true, error:null,
      notice:"Modalità demo Artifact: nessuna email è stata realmente inviata. In un ambiente con backend configurato questa richiesta userebbe Supabase Auth (resetPasswordForEmail). Puoi comunque aprire la pagina \"Imposta una nuova password\" per provare il flusso demo."
    };
  }

  async updatePassword(newPassword){
    const user = this._currentUserRecord();
    if(!user) return { ok:false, error:"Nessuna sessione demo attiva.", notice:null };
    const policy = validatePasswordPolicy(newPassword);
    if(!policy.ok) return { ok:false, error:policy.error, notice:null };
    const credentials = await hashNewPassword(newPassword);
    Object.assign(user, credentials);
    this._persist();
    this._notifyAuthChange("USER_UPDATED");
    return { ok:true, error:null, notice:"Modalità demo Artifact: password aggiornata solo in questo browser. " + DEMO_PASSWORD_WARNING };
  }

  /** @param {(event:string, user:{id:string,email:string}|null)=>void} callback */
  onAuthStateChange(callback){
    this._authListeners.add(callback);
    return () => this._authListeners.delete(callback);
  }

  async getProfile(userId){
    return this._store.profiles[userId] || null;
  }
  async updateProfile(userId, patch){
    const existing = this._store.profiles[userId];
    if(!existing) return { ok:false, error:"Profilo demo non trovato." };
    Object.assign(existing, patch || {});
    this._persist();
    return { ok:true, error:null };
  }

  async listMyAuctions(){
    const userId = this._store.sessionUserId;
    if(!userId) return [];
    // Revisione alpha.4-r1: include anche le aste dove l'utente compare in members con ruolo
    // "member" (join by code), non solo quelle di cui è owner — vedi
    // MULTIPLAYER_SESSION_DESIGN_alpha4_r1.md §1.5/§5. L'isolamento fra account resta identico:
    // un'asta compare SOLO se l'utente ha davvero una riga in members, mai per default. BLOCKER A
    // (alpha.4-r2): usa la stessa helper centralizzata membershipOf() di ogni altro read path, mai
    // una seconda implementazione del filtro.
    return this._store.auctions
      .map(a => ({ a, membership: membershipOf(a, userId) }))
      .filter(x => x.membership)
      .map(x => this._toSummary(x.a, x.membership))
      .sort((a,b)=> b.updatedAt.localeCompare(a.updatedAt));
  }

  /** BLOCKER A (audit indipendente su r1): richiede la riga di membership GIÀ risolta dal
   *  chiamante (mai un secondo lookup interno, mai un fallback che assegni un ruolo a chi non ha
   *  alcuna membership reale — la r1 lo faceva: `mine ? mine.role : (a.ownerId===userId ? "owner" :
   *  "member")`, che assegnava silenziosamente "member" a QUALUNQUE utente senza membership,
   *  incluso un outsider completo). Ogni chiamante deve aver già verificato la visibilità tramite
   *  membershipOf() PRIMA di arrivare qui. */
  _toSummary(a, membership){
    return {
      id:a.id, name:a.name, season:a.season||null, status:a.status,
      ownerId:a.ownerId, myRole: membership.role,
      createdAt:a.createdAt, updatedAt:a.updatedAt
    };
  }

  async createAuction({name, season}){
    const userId = this._store.sessionUserId;
    if(!userId) return { ok:false, auction:null, error:"Devi accedere (anche in modalità demo) prima di creare un'asta." };
    const trimmed = String(name||"").trim();
    if(!trimmed) return { ok:false, auction:null, error:"Il nome dell'asta è obbligatorio." };
    const now = nowISO();
    const ownerMembership = { userId, role:"owner", joinedAt:now };
    const a = {
      id: uid("demoauction"), ownerId:userId, name:trimmed, season: season||null, status:"active", createdAt:now, updatedAt:now,
      members: [ownerMembership], // parità col trigger handle_new_auction() lato reale (0001)
      joinCode: null // {hash, generatedAt} | null — MAI il plaintext, vedi rotateJoinCode()
    };
    this._store.auctions.push(a);
    this._persist();
    return { ok:true, auction: this._toSummary(a, ownerMembership), error:null };
  }

  async renameAuction(auctionId, name){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, error:"Solo il proprietario può rinominare l'asta." };
    const trimmed = String(name||"").trim();
    if(!trimmed) return { ok:false, error:"Il nome dell'asta è obbligatorio." };
    a.name = trimmed; a.updatedAt = nowISO();
    this._persist();
    return { ok:true, error:null };
  }

  async archiveAuction(auctionId){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, error:"Solo il proprietario può archiviare l'asta." };
    a.status = "archived"; a.updatedAt = nowISO();
    this._persist();
    return { ok:true, error:null };
  }

  // BLOCKER A (audit indipendente su r1): PRIMA di questa correzione, questo metodo restituiva la
  // summary per QUALUNQUE auctionId esistente, indipendentemente da chi stesse chiedendo — un
  // outsider che conoscesse/indovinasse un id poteva leggere nome/stagione/stato/ownerId di un'asta
  // altrui. Su SupabaseBackendAdapter questo era già impossibile per costruzione (RLS
  // auctions_select_owner_or_member, 0003, invariata — un SELECT su una riga non visibile restituisce
  // semplicemente nessuna riga). Ora verifica la visibilità con la STESSA helper centralizzata
  // (membershipOf()) usata da ogni altro read path, e restituisce null per un chiamante non owner/
  // member — mai un errore diverso, mai un dettaglio che riveli l'esistenza dell'asta.
  async getAuctionSummary(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return null;
    const membership = membershipOf(a, userId);
    if(!membership) return null;
    return this._toSummary(a, membership);
  }

  // BLOCKER A (audit indipendente su r1): stesso difetto/stessa correzione di getAuctionSummary()
  // sopra — PRIMA, questo metodo non verificava alcuna appartenenza: un outsider con un auctionId
  // noto poteva leggere l'INTERO stato applicativo dell'asta (roster, config, storico). Su
  // SupabaseBackendAdapter questo era già impossibile (RLS auction_states_select_related, 0003,
  // invariata). Ora verifica la visibilità PRIMA di leggere lo stato, mai dopo.
  async loadAuctionState(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return null;
    if(!membershipOf(a, userId)) return null;
    const envelope = this._store.auctionStates[auctionId];
    return envelope ? JSON.parse(JSON.stringify(envelope)) : null;
  }

  // Revisione r11 (audit, dead-end di una nuova asta): questo metodo NON verificava l'ownership —
  // a differenza di renameAuction()/archiveAuction() sopra, che lo fanno da sempre. Su
  // SupabaseBackendAdapter l'equivalente è già imposto dalla RLS (auction_states_insert_owner_only/
  // auction_states_update_owner_only, 0003_rls_policies.sql, MAI modificata) — questo controllo
  // chiude lo stesso gap qui, per parità reale fra i due adapter (obiettivo 7 del round), non solo
  // per estetica: senza di esso, un utente diverso dal proprietario potrebbe inizializzare/
  // sovrascrivere arbitrariamente lo stato di un'asta altrui nella build demo.
  async saveAuctionState(auctionId, state, expectedRevision){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, conflict:false, revision:null, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, conflict:false, revision:null, error:"Solo il proprietario può salvare lo stato di questa asta." };
    const current = this._store.auctionStates[auctionId] || null;
    const currentRevision = current ? current.revision : 0;
    if(expectedRevision !== currentRevision){
      return { ok:false, conflict:true, revision: currentRevision, error:"Un'altra scheda ha salvato una versione più recente di questa asta." };
    }
    const nextRevision = currentRevision + 1;
    this._store.auctionStates[auctionId] = {
      auctionId, revision: nextRevision, updatedAt: nowISO(), state: JSON.parse(JSON.stringify(state))
    };
    a.updatedAt = nowISO();
    this._persist();
    return { ok:true, conflict:false, revision: nextRevision, error:null };
  }

  // ---------------- Lobby multiplayer / join-code (round alpha.4-r1) ----------------
  // Parità di comportamento osservabile con SupabaseBackendAdapter (vedi BackendAdapter.js e
  // MULTIPLAYER_SESSION_DESIGN_alpha4_r1.md §5): stesse forme di ritorno, stesse regole di
  // autorizzazione (owner-only per generate/rotate/revoke/status/remove, member-or-owner per la
  // lista, auth.uid()-equivalente — qui sessionUserId — mai un userId passato dal chiamante per il
  // join). Il banner demo resta invariato: nessun secondo utente "davvero" connesso, il flusso a due
  // account avviene nello stesso store/browser (stesso principio di test_multi_auction.js).

  async joinAuctionByCode(rawCode){
    const userId = this._store.sessionUserId;
    if(!userId) return { ok:false, auctionId:null, alreadyMember:false, error:"Devi accedere (anche in modalità demo) prima di partecipare a un'asta." };
    const canonical = canonicalizeJoinCode(rawCode);
    if(!canonical) return { ok:false, auctionId:null, alreadyMember:false, error:"Codice non valido." };
    const hash = await hashJoinCodeCanonical(canonical);
    const a = this._store.auctions.find(x => x.joinCode && x.joinCode.hash === hash);
    if(!a) return { ok:false, auctionId:null, alreadyMember:false, error:"Codice non valido o non più attivo." };
    if(a.status === "archived") return { ok:false, auctionId:null, alreadyMember:false, error:"Questa asta è archiviata e non accetta nuovi partecipanti." };
    const members = membersOf(a);
    a.members = members;
    const already = members.some(m => m.userId === userId);
    if(!already) members.push({ userId, role:"member", joinedAt: nowISO() });
    this._persist();
    return { ok:true, auctionId:a.id, alreadyMember: already, error:null };
  }

  async listAuctionMembers(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) throw new Error("Asta demo non trovata.");
    if(!membershipOf(a, userId)) throw new Error("Non sei membro di questa asta.");
    const members = membersOf(a);
    return members
      .slice()
      .sort((x,y) => (y.role==="owner")-(x.role==="owner") || String(x.joinedAt).localeCompare(String(y.joinedAt)))
      .map(m => {
        const profile = this._store.profiles[m.userId];
        return { userId:m.userId, displayName: profile ? profile.displayName : "(sconosciuto)", role:m.role, joinedAt:m.joinedAt };
      });
  }

  // Hardening (audit indipendente su r1): PRIMA di questa correzione, una collisione rara fra
  // l'hash appena generato e quello GIÀ attivo su un'altra asta non veniva mai rilevata — la nuova
  // riga si sarebbe scritta comunque, e da quel momento un join con quel codice avrebbe risolto
  // sempre e solo la PRIMA asta trovata da .find() (ordine di inserimento nell'array), rendendo il
  // codice della seconda asta silenziosamente inutilizzabile. Ora ritenta fino a 5 volte su
  // collisione — stesso comportamento/stesso limite del retry lato SQL
  // (rotate_auction_join_code(), 0012, BLOCKER B) — mai un'assegnazione silenziosa dello stesso
  // bearer code a due aste.
  async rotateJoinCode(auctionId){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, code:null, generatedAt:null, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, code:null, generatedAt:null, error:"Solo il proprietario può generare/ruotare il codice di invito." };
    let plain, hash;
    let attempt = 0;
    do{
      attempt++;
      plain = generateJoinCodePlain();
      hash = await hashJoinCodeCanonical(canonicalizeJoinCode(plain));
      const collision = this._store.auctions.some(x => x.id !== auctionId && x.joinCode && x.joinCode.hash === hash);
      if(!collision) break;
      if(attempt >= 5) return { ok:false, code:null, generatedAt:null, error:"Impossibile generare un codice univoco dopo 5 tentativi." };
    }while(true);
    const generatedAt = nowISO();
    a.joinCode = { hash, generatedAt };
    this._persist();
    return { ok:true, code:plain, generatedAt, error:null };
  }

  async revokeJoinCode(auctionId){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, error:"Solo il proprietario può revocare il codice di invito." };
    a.joinCode = null;
    this._persist();
    return { ok:true, error:null };
  }

  async getJoinCodeStatus(auctionId){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, active:false, generatedAt:null, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, active:false, generatedAt:null, error:"Solo il proprietario può leggere lo stato del codice di invito." };
    return { ok:true, active: !!a.joinCode, generatedAt: a.joinCode ? a.joinCode.generatedAt : null, error:null };
  }

  // Round alpha.5-r1: rimuove QUALUNQUE assegnazione di seat del membro rimosso, per QUALUNQUE asta —
  // parità con il trigger private.handle_auction_member_removed() lato reale (0013), che scatta su
  // OGNI DELETE da auction_members (self-leave e owner-remove passano dalla stessa policy 0012).
  // Invariante mantenuto qui: auction_seats[].assignedUserId, se non null, punta sempre a un membro
  // reale di QUELLA stessa asta, mai un riferimento stantio.
  _unassignSeatsForRemovedMember(a, userId){
    seatsOf(a).forEach(s => { if(s.assignedUserId === userId){ s.assignedUserId = null; s.updatedAt = nowISO(); } });
  }

  // A7-P1-8 (D3 §4/§8/§12/§13, migrazione 0017 parte B): fuori da 'setup' (inclusa 'closed', D3 §8
  // Model C1), un membro che controlla ATTUALMENTE una seat assegnata non può essere rimosso/lasciare
  // l'asta — l'unico modo per liberarlo resta la sostituzione atomica del controller
  // (assignMemberToSeat sopra). Nessun riferimento a nomine/offerte/acquisti: la regola dipende SOLO
  // da lifecycle e assegnazione corrente, mai da storico (D3: "History is intentionally irrelevant
  // under the final D3 model") — parità con auction_members_delete_self_or_owner lato reale (0017).
  _departureBlockedByAssignment(a, userId){
    const session = liveSessionOf(a);
    const lifecycle = session ? session.lifecycle : null;
    if(!lifecycle || lifecycle === "setup") return false;
    return seatsOf(a).some(s => s.assignedUserId === userId);
  }

  async removeAuctionMember(auctionId, memberUserId){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, error:"Solo il proprietario può rimuovere un membro." };
    const members = membersOf(a);
    a.members = members;
    const idx = members.findIndex(m => m.userId === memberUserId);
    if(idx === -1) return { ok:false, error:"Membro non trovato." };
    if(members[idx].role === "owner") return { ok:false, error:"Il proprietario non può essere rimosso." };
    if(this._departureBlockedByAssignment(a, memberUserId)){
      return { ok:false, error:"Questo membro controlla ancora una squadra assegnata — riassegna prima la squadra a un altro membro." };
    }
    members.splice(idx, 1);
    this._unassignSeatsForRemovedMember(a, memberUserId);
    this._persist();
    return { ok:true, error:null };
  }

  async leaveAuction(auctionId){
    const userId = this._store.sessionUserId;
    if(!userId) return { ok:false, error:"Devi accedere per lasciare un'asta." };
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, error:"Asta demo non trovata." };
    const members = membersOf(a);
    a.members = members;
    const idx = members.findIndex(m => m.userId === userId);
    if(idx === -1) return { ok:false, error:"Non risulti membro di questa asta." };
    if(members[idx].role === "owner") return { ok:false, error:"Il proprietario non può abbandonare la propria asta." };
    if(this._departureBlockedByAssignment(a, userId)){
      return { ok:false, error:"Non puoi abbandonare l'asta mentre controlli una squadra assegnata. Chiedi al proprietario di sostituirti." };
    }
    members.splice(idx, 1);
    this._unassignSeatsForRemovedMember(a, userId);
    this._persist();
    return { ok:true, error:null };
  }

  // ---------------- Live auction: seat/squadra (round alpha.5-r1) ----------------
  // Parità di comportamento osservabile con SupabaseBackendAdapter/0013 (vedi
  // LIVE_AUCTION_DESIGN_alpha5_r1.md §2): stesse tre condizioni per assign_member_to_seat, stessa
  // idempotenza, owner-only per ogni scrittura, mai un'auto-assegnazione lato member.

  async createAuctionSeat(auctionId, label){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { ok:false, seatId:null, seatNo:null, label:null, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, seatId:null, seatNo:null, label:null, error:"Solo il proprietario può creare una squadra." };
    const trimmed = String(label||"").trim();
    if(!trimmed || trimmed.length > 80) return { ok:false, seatId:null, seatNo:null, label:null, error:"Il nome della squadra deve essere lungo fra 1 e 80 caratteri." };
    // BLOCKER B (audit indipendente su r1, decisione esplicita): topologia seat CONGELATA dopo che il
    // lifecycle esce da 'setup' — parità con private.transition_live_session()/create_auction_seat()
    // lato reale (0013), simmetrico al vincolo già esistente su deleteAuctionSeat().
    const existingSession = liveSessionOf(a);
    if(existingSession && existingSession.lifecycle !== "setup"){
      return { ok:false, seatId:null, seatNo:null, label:null, error:"Non è possibile creare una squadra mentre la sessione live non è in preparazione (stato attuale: " + existingSession.lifecycle + ")." };
    }
    const seats = seatsOf(a);
    a.seats = seats;
    const nextSeatNo = seats.reduce((max,s)=>Math.max(max, s.seatNo), 0) + 1;
    const now = nowISO();
    const seat = { id: uid("seat"), auctionId, seatNo: nextSeatNo, label: trimmed, assignedUserId: null, createdAt: now, updatedAt: now };
    seats.push(seat);
    this._persist();
    return { ok:true, seatId: seat.id, seatNo: seat.seatNo, label: seat.label, error:null };
  }

  /** Restituisce {auction, seat} per un dato seatId, o {auction:null, seat:null} se non trovato —
   *  unico punto di risoluzione, mai una seconda ricerca divergente (stesso principio di
   *  membershipOf()). */
  _findSeat(seatId){
    for(const a of this._store.auctions){
      const seat = seatsOf(a).find(s => s.id === seatId);
      if(seat) return { auction:a, seat };
    }
    return { auction:null, seat:null };
  }

  async deleteAuctionSeat(seatId){
    const { auction:a, seat } = this._findSeat(seatId);
    if(!a || !seat) return { ok:false, error:"Squadra non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, error:"Solo il proprietario può eliminare una squadra." };
    if(seat.assignedUserId) return { ok:false, error:"Questa squadra è assegnata a un membro — rimuovi prima l'assegnazione." };
    const session = liveSessionOf(a);
    if(session && session.lifecycle !== "setup"){
      return { ok:false, error:"Non è possibile eliminare una squadra mentre la sessione live non è in preparazione (stato attuale: " + session.lifecycle + ")." };
    }
    a.seats = seatsOf(a).filter(s => s.id !== seatId);
    this._persist();
    return { ok:true, error:null };
  }

  // BLOCKER C (audit indipendente su alpha.5-r1): mai un nome di visualizzazione risolto qui — parità
  // con SupabaseBackendAdapter (che non può leggere `profiles` di terzi). Un outsider (non owner/
  // member) riceve `[]`, mai un'eccezione: allineato alla semantica di una SELECT diretta filtrata da
  // RLS lato reale (una policy che nega tutte le righe restituisce un array vuoto, non un errore) —
  // le due implementazioni devono avere lo STESSO comportamento osservabile, mai una che lancia e
  // l'altra no.
  async listAuctionSeats(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return [];
    if(!membershipOf(a, userId)) return [];
    return seatsOf(a).slice().sort((x,y)=>x.seatNo-y.seatNo).map(s => ({
      id: s.id, seatNo: s.seatNo, label: s.label,
      assignedUserId: s.assignedUserId || null
    }));
  }

  async assignMemberToSeat(seatId, userId){
    const { auction:a, seat } = this._findSeat(seatId);
    if(!a || !seat) return { ok:false, error:"Squadra non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, error:"Solo il proprietario può assegnare una squadra." };
    if(!userId) return { ok:false, error:"userId è obbligatorio." };

    // Condizione 1: il target deve essere owner o member di QUESTA asta. Verificata PRIMA della
    // diramazione di lifecycle sotto — stesso ordine lato reale (0017: il lock sul membro precede
    // sempre quello sulla seat, per non riaprire BLOCKER B/r3, 0013:119-143).
    if(!membershipOf(a, userId)) return { ok:false, error:"L'utente indicato non è owner né membro di questa asta." };

    // A7-P1-8 (migrazione 0017, ristretta da 0018): null→B (attivazione di una seat mai assegnata) E
    // "A→A" (nessun cambiamento reale — l'utente indicato è già l'assegnatario corrente) restano
    // ENTRAMBE congelate fuori da 'setup', invariato rispetto ad alpha.5-r4-r1. Solo una sostituzione
    // GENUINA (A→B con A<>B) resta ammessa anche in 'running'/'paused', congelata solo in 'closed' —
    // parità con assign_member_to_seat() lato reale (0018). 0017 esentava per errore anche il ramo
    // "A→A" da qualunque controllo di lifecycle (mai discusso/approvato dal design D3 §16, che elenca
    // esplicitamente solo "A→B" come mutazione ammessa fuori da 'setup') — scarto esposto dal real-gate
    // A7-P1-8V (un test reale pre-esistente, invariato da prima di 0017, richiama questa funzione con
    // l'utente già assegnato durante 'running' aspettandosi il rifiuto) e corretto qui.
    const session = liveSessionOf(a);
    const lifecycle = session ? session.lifecycle : null;
    if(!seat.assignedUserId || seat.assignedUserId === userId){
      if(lifecycle && lifecycle !== "setup"){
        return { ok:false, error:"Non è possibile assegnare una squadra mentre la sessione live non è in preparazione (stato attuale: " + lifecycle + ")." };
      }
    } else {
      if(lifecycle === "closed"){
        return { ok:false, error:"Non è possibile sostituire il controller di una squadra dopo che la sessione live è stata chiusa." };
      }
    }

    // Condizione 3 (invariata): il target non deve già controllare un'ALTRA squadra in questa stessa asta.
    const otherSeat = seatsOf(a).find(s => s.assignedUserId === userId && s.id !== seatId);
    if(otherSeat) return { ok:false, error:"Questo membro controlla già un'altra squadra in questa asta (\"" + otherSeat.label + "\")." };

    seat.assignedUserId = userId;
    seat.updatedAt = nowISO();
    this._persist();
    return { ok:true, error:null };
  }

  async unassignMemberFromSeat(seatId){
    const { auction:a, seat } = this._findSeat(seatId);
    if(!a || !seat) return { ok:false, error:"Squadra non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { ok:false, error:"Solo il proprietario può rimuovere un'assegnazione." };
    // Round alpha.5-r4-r1: stessa restrizione di assignMemberToSeat() sopra.
    const session = liveSessionOf(a);
    if(session && session.lifecycle !== "setup"){
      return { ok:false, error:"Non è possibile rimuovere un'assegnazione mentre la sessione live non è in preparazione (stato attuale: " + session.lifecycle + ")." };
    }
    seat.assignedUserId = null;
    seat.updatedAt = nowISO();
    this._persist();
    return { ok:true, error:null };
  }

  // ---------------- Live auction: lifecycle della sessione (round alpha.5-r1) ----------------
  // Concorrenza ottimistica esplicita (expectedRevision), stesso principio di saveAuctionState() —
  // MAI un retry automatico su conflitto: il chiamante riceve {conflict:true} e decide.

  async loadLiveSession(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return null;
    if(!membershipOf(a, userId)) return null;
    const session = liveSessionOf(a);
    if(!session) return null;
    // Estensione round alpha.5-r4-r1: campi nomination/bid/deadline sempre presenti nella risposta
    // (mai `undefined` per una sessione pre-r4/senza nomina in corso — coalescenza esplicita a null/0,
    // parità con l'invariante nomination_consistency lato reale, ramo "nessuna nomina in corso").
    return {
      auctionId, lifecycle: session.lifecycle, revision: session.revision,
      nominationStatus: session.nominationStatus || null,
      currentNominationId: session.currentNominationId || null,
      nominatedPlayerFantacalcioId: session.nominatedPlayerFantacalcioId != null ? session.nominatedPlayerFantacalcioId : null,
      nominatingSeatId: session.nominatingSeatId || null,
      currentHighestBid: session.currentHighestBid != null ? session.currentHighestBid : null,
      highestBidderSeatId: session.highestBidderSeatId || null,
      bidSeq: session.bidSeq || 0,
      deadlineAt: session.deadlineAt || null,
      // A7-P1-2 (§16): parità col reale (SupabaseBackendAdapter) — session.closedAt è già tracciato
      // internamente da _transitionLiveSession(), semplicemente non era ancora esposto qui.
      closedAt: session.closedAt || null
    };
  }

  _requireOwnerForLiveSession(auctionId){
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return { a:null, error:"Asta demo non trovata." };
    if(a.ownerId !== this._store.sessionUserId) return { a:null, error:"Solo il proprietario può modificare la sessione live." };
    return { a, error:null };
  }

  async startLiveSession(auctionId, expectedRevision){
    const { a, error } = this._requireOwnerForLiveSession(auctionId);
    if(!a) return { ok:false, conflict:false, revision:null, lifecycle:null, error };
    // BLOCKER B (audit indipendente su r1): rifiuto ESPLICITO di expectedRevision NULL — parità con
    // start_live_session()/private.transition_live_session() lato reale (0013), mai un bypass del CAS.
    if(expectedRevision === null || expectedRevision === undefined){
      return { ok:false, conflict:false, revision:null, lifecycle:null, error:"expectedRevision è obbligatorio (nessun bypass del controllo di concorrenza)." };
    }
    if(seatsOf(a).length === 0){
      return { ok:false, conflict:false, revision:null, lifecycle:null, error:"Crea almeno una squadra prima di avviare la sessione live." };
    }
    // Round alpha.5-r4-r1: le regole live sono un prerequisito esplicito — parità con
    // start_live_session() lato reale (0014).
    if(!liveRulesOf(a)){
      return { ok:false, conflict:false, revision:null, lifecycle:null, error:"Configura le regole live prima di avviare la sessione." };
    }
    // Round alpha.5-r5-r1: le regole di rosa/budget sono un prerequisito esplicito quanto le regole
    // live — parità con start_live_session() lato reale (0015).
    if(!rosterRulesOf(a)){
      return { ok:false, conflict:false, revision:null, lifecycle:null, error:"Configura le regole di rosa/budget prima di avviare la sessione." };
    }
    const session = liveSessionOf(a);
    if(!session){
      if(expectedRevision !== 0){
        return { ok:false, conflict:true, revision:0, lifecycle:"setup", error:"Un'altra scheda ha già inizializzato la sessione live di questa asta." };
      }
      a.liveSession = {
        lifecycle:"running", revision:1, startedAt: nowISO(), pausedAt:null, closedAt:null,
        nominationStatus:null, currentNominationId:null, nominatedPlayerFantacalcioId:null,
        nominatingSeatId:null, currentHighestBid:null, highestBidderSeatId:null, bidSeq:0, deadlineAt:null
      };
      this._persist();
      return { ok:true, conflict:false, revision:1, lifecycle:"running", error:null };
    }
    if(expectedRevision !== session.revision){
      return { ok:false, conflict:true, revision:session.revision, lifecycle:session.lifecycle, error:"Un'altra scheda ha già modificato la sessione live di questa asta." };
    }
    if(session.lifecycle !== "setup"){
      return { ok:false, conflict:false, revision:session.revision, lifecycle:session.lifecycle, error:"La sessione live non è in preparazione (stato attuale: " + session.lifecycle + ")." };
    }
    session.lifecycle = "running"; session.revision += 1; session.startedAt = nowISO();
    this._persist();
    return { ok:true, conflict:false, revision:session.revision, lifecycle:"running", error:null };
  }

  async _transitionLiveSession(auctionId, expectedRevision, fromStates, to, timestampField){
    const { a, error } = this._requireOwnerForLiveSession(auctionId);
    if(!a) return { ok:false, conflict:false, revision:null, lifecycle:null, error };
    if(expectedRevision === null || expectedRevision === undefined){
      return { ok:false, conflict:false, revision:null, lifecycle:null, error:"expectedRevision è obbligatorio (nessun bypass del controllo di concorrenza)." };
    }
    const session = liveSessionOf(a);
    if(!session) return { ok:false, conflict:false, revision:null, lifecycle:null, error:"La sessione live di questa asta non è mai stata avviata." };
    if(expectedRevision !== session.revision){
      return { ok:false, conflict:true, revision:session.revision, lifecycle:session.lifecycle, error:"Un'altra scheda ha già modificato la sessione live di questa asta." };
    }
    if(!fromStates.includes(session.lifecycle)){
      return { ok:false, conflict:false, revision:session.revision, lifecycle:session.lifecycle, error:"Transizione non valida da " + session.lifecycle + "." };
    }
    // Round alpha.5-r4-r1: rifiuta pause/close se una nomina è open o pending_settlement — parità con
    // private.transition_live_session() lato reale (0014). resume (-> "running") non è mai ristretto:
    // per invariante una sessione "paused" ha sempre nominationStatus null.
    if((to === "paused" || to === "closed") && session.nominationStatus){
      return { ok:false, conflict:false, revision:session.revision, lifecycle:session.lifecycle,
        error:"Non è possibile mettere in pausa o chiudere la sessione mentre una nomina è aperta o in attesa di finalizzazione (stato nomina attuale: " + session.nominationStatus + ")." };
    }
    session.lifecycle = to; session.revision += 1;
    if(timestampField) session[timestampField] = nowISO();
    this._persist();
    return { ok:true, conflict:false, revision:session.revision, lifecycle:to, error:null };
  }

  async pauseLiveSession(auctionId, expectedRevision){
    return this._transitionLiveSession(auctionId, expectedRevision, ["running"], "paused", "pausedAt");
  }
  async resumeLiveSession(auctionId, expectedRevision){
    return this._transitionLiveSession(auctionId, expectedRevision, ["paused"], "running", "startedAt");
  }
  async closeLiveSession(auctionId, expectedRevision){
    return this._transitionLiveSession(auctionId, expectedRevision, ["running","paused"], "closed", "closedAt");
  }

  // ---------------- Live auction: nomina/offerta/timer (round alpha.5-r4-r1) ----------------
  // Parità di comportamento osservabile con SupabaseBackendAdapter/0014: stessa idempotenza via
  // commandId, stesso ordine "verifica accesso -> replay -> business logic" (la revoca di accesso
  // prevale sempre sul replay), stesso this._clock() come UNICA autorità del tempo (mai
  // `new Date()` diretto in questi cinque metodi, cosi' i test possono iniettare un clock
  // deterministico), stessa formula di scadenza reset-on-bid.

  async loadLiveRules(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return null;
    if(!membershipOf(a, userId)) return null;
    const rules = liveRulesOf(a);
    if(!rules) return null;
    return {
      auctionId, openingPrice: rules.openingPrice, initialTimerSeconds: rules.initialTimerSeconds,
      resetOnBid: rules.resetOnBid, resetTimerSeconds: rules.resetTimerSeconds, revision: rules.revision
    };
  }

  async configureLiveRules(auctionId, rules, expectedRevision){
    const { a, error } = this._requireOwnerForLiveSession(auctionId);
    if(!a) return { ok:false, conflict:false, revision:null, error };
    if(expectedRevision === null || expectedRevision === undefined){
      return { ok:false, conflict:false, revision:null, error:"expectedRevision è obbligatorio (nessun bypass del controllo di concorrenza)." };
    }
    const openingPrice = rules && rules.openingPrice;
    const initialTimerSeconds = rules && rules.initialTimerSeconds;
    const resetOnBid = rules && rules.resetOnBid;
    const resetTimerSeconds = rules && rules.resetTimerSeconds;
    // DEMO PARITY (round alpha.5-r4-r2, audit indipendente): opening_price/initial_timer_seconds/
    // reset_timer_seconds sono tipizzati `integer` lato SQL (0014) — un valore reale con parte
    // decimale (es. 10.5) verrebbe rifiutato dalla connessione reale (SupabaseBackendAdapter) durante
    // il parsing del parametro RPC, PRIMA ancora della logica applicativa. Number.isInteger() qui
    // riproduce lo stesso vincolo, senza introdurre alcun nuovo limite superiore o default.
    if(!(Number.isInteger(openingPrice) && openingPrice > 0)) return { ok:false, conflict:false, revision:null, error:"Il prezzo di apertura deve essere un numero intero positivo." };
    if(!(Number.isInteger(initialTimerSeconds) && initialTimerSeconds > 0)) return { ok:false, conflict:false, revision:null, error:"Il timer iniziale deve essere un numero intero positivo." };
    if(!(Number.isInteger(resetTimerSeconds) && resetTimerSeconds > 0)) return { ok:false, conflict:false, revision:null, error:"Il timer di reset deve essere un numero intero positivo." };
    if(typeof resetOnBid !== "boolean") return { ok:false, conflict:false, revision:null, error:"reset_on_bid è obbligatorio." };

    const session = liveSessionOf(a);
    if(session && session.lifecycle !== "setup"){
      return { ok:false, conflict:false, revision:null, error:"Non modificabile fuori da setup (stato attuale: " + session.lifecycle + ")." };
    }

    const existing = liveRulesOf(a);
    if(!existing){
      if(expectedRevision !== 0){
        return { ok:false, conflict:true, revision:0, error:"Un'altra scheda ha già inizializzato le regole live." };
      }
      a.liveRules = { openingPrice, initialTimerSeconds, resetOnBid, resetTimerSeconds, revision:1, updatedAt: nowISO() };
      this._persist();
      return { ok:true, conflict:false, revision:1, error:null };
    }
    if(expectedRevision !== existing.revision){
      return { ok:false, conflict:true, revision:existing.revision, error:"Un'altra scheda ha già modificato le regole live." };
    }
    existing.openingPrice = openingPrice; existing.initialTimerSeconds = initialTimerSeconds;
    existing.resetOnBid = resetOnBid; existing.resetTimerSeconds = resetTimerSeconds;
    existing.revision += 1; existing.updatedAt = nowISO();
    this._persist();
    return { ok:true, conflict:false, revision:existing.revision, error:null };
  }

  async nominatePlayer(auctionId, seatId, fantacalcioId, expectedRevision, commandId){
    const { a, error } = this._requireOwnerForLiveSession(auctionId);
    const fail = (msg, extra) => Object.assign({ ok:false, conflict:false, revision:null, nominationId:null, nominationSeq:null, openingPrice:null, deadlineAt:null, error:msg }, extra||{});
    if(!a) return fail(error);
    if(expectedRevision === null || expectedRevision === undefined){
      return fail("expectedRevision è obbligatorio (nessun bypass del controllo di concorrenza).");
    }
    if(!commandId) return fail("commandId è obbligatorio.");
    if(!isValidUuid(commandId)) return fail("commandId deve essere un UUID valido (parità col tipo SQL `uuid` di 0014).");

    const nominations = nominationsOf(a);
    a.nominations = nominations;
    const existingCmd = nominations.find(n => n.commandId === commandId);
    if(existingCmd){
      if(existingCmd.nominatingSeatId === seatId && existingCmd.playerFantacalcioId === fantacalcioId){
        return { ok:true, conflict:false, revision:existingCmd.openedAtRevision, nominationId:existingCmd.id,
          nominationSeq:existingCmd.nominationSeq, openingPrice:existingCmd.openingPrice, deadlineAt:existingCmd.deadlineAt, error:null };
      }
      return fail("commandId già usato con un payload diverso.");
    }

    const seat = seatsOf(a).find(s => s.id === seatId);
    if(!seat) return fail("Squadra non trovata per questa asta.");

    const session = liveSessionOf(a);
    if(!session || session.lifecycle !== "running") return fail("La sessione live non è in corso.");
    if(expectedRevision !== session.revision){
      return fail("Un'altra scheda ha già modificato la sessione live.", { conflict:true, revision:session.revision });
    }
    if(session.nominationStatus) return fail("Esiste già una nomina " + session.nominationStatus + ".");

    const rules = liveRulesOf(a);
    if(!rules) return fail("Regole live non configurate per questa asta.");

    // Round alpha.5-r5-r1: parità con nominate_player() lato reale (0015) — roster rules
    // prerequisito esplicito, e nessuna nomina su un player già presente nel ledger acquisti.
    const rosterRules = rosterRulesOf(a);
    if(!rosterRules) return fail("Regole di rosa/budget non configurate per questa asta.");

    const role = readCatalogPlayerRole(fantacalcioId);
    if(!role || !["POR","DIF","CEN","ATT"].includes(role)){
      return fail("Player inesistente o non corrente nel catalogo.");
    }

    if(purchasesOf(a).some(p => p.playerFantacalcioId === fantacalcioId)){
      return fail("Questo giocatore è già stato acquistato in questa asta.");
    }

    // A7-P1-2 (parità con la guardia SQL di migration 0016): rifiuta SOLO quando OGNI squadra
    // partecipante (seat con assignedUserId valorizzato — una seat mai assegnata non è mai stata un
    // partecipante reale, e zero squadre partecipanti non attiva mai questa guardia, stessa semantica
    // della RPC reale) ha, per OGNI ruolo classico, un conteggio acquisti >= alla capacità configurata
    // — mai per una singola squadra/ruolo pieno, mai per zero squadre assegnate. Confronto fail-safe
    // (>=, mai =), stessa scelta della guardia reale — vedi ROSTER_ANOMALY lato UI per la
    // classificazione di sola lettura di un'eventuale riga oltre-capacità.
    const participatingSeats = seatsOf(a).filter(s => s.assignedUserId);
    if(participatingSeats.length > 0){
      const purchases = purchasesOf(a);
      const roleCapacityOf = { POR: rosterRules.slotPor, DIF: rosterRules.slotDif, CEN: rosterRules.slotCen, ATT: rosterRules.slotAtt };
      const isUnderfilled = participatingSeats.some(s =>
        ["POR","DIF","CEN","ATT"].some(r =>
          purchases.filter(p => p.seatId === s.id && p.roleAtPurchase === r).length < roleCapacityOf[r]
        )
      );
      if(!isUnderfilled){
        return fail("Rosa al completo per tutte le squadre partecipanti — nessun altro giocatore può essere nominato in questa asta.");
      }

      // A7-P1-10 (parità con la guardia SQL di migration 0019): raggiungibile SOLO se la guardia a
      // matrice intera appena sopra non ha già rifiutato (mai un cambiamento al suo esito/precedenza).
      // Rifiuta SOLO quando NESSUNA squadra partecipante ha ancora capacità residua per il ruolo
      // specifico del giocatore nominato (`role`, la STESSA variabile che diventerà
      // playerRoleAtNomination poco più sotto — mai un ruolo ri-derivato o divergente da quello che
      // placeBid applicherà in seguito). Fuori scope: budget/credito residuo, valutazione giocatore,
      // importo di offerta, storico nomine — solo capacità di rosa per ruolo, stessa mappa
      // roleCapacityOf già usata sopra.
      const roleAvailableSeat = participatingSeats.some(s =>
        purchases.filter(p => p.seatId === s.id && p.roleAtPurchase === role).length < roleCapacityOf[role]
      );
      if(!roleAvailableSeat){
        return fail("Ruolo " + role + " esaurito per tutte le squadre partecipanti — nessun altro giocatore di questo ruolo può essere nominato in questa asta.");
      }
    }

    const nextSeq = nominations.reduce((max,n)=>Math.max(max, n.nominationSeq), 0) + 1;
    const nextRevision = session.revision + 1;
    const decisionTime = this._clock();
    const deadlineAt = new Date(decisionTime.getTime() + rules.initialTimerSeconds * 1000).toISOString();
    const nominationId = uid("nomination");

    nominations.push({
      id: nominationId, nominationSeq: nextSeq, playerFantacalcioId: fantacalcioId, playerRoleAtNomination: role,
      nominatingSeatId: seatId, openingPrice: rules.openingPrice, deadlineAt, status:"open",
      finalAmount: null, finalSeatId: null, commandId, openedAt: nowISO(), openedAtRevision: nextRevision,
      closedAt: null, closedAtRevision: null
    });

    session.revision = nextRevision; session.nominationStatus = "open"; session.currentNominationId = nominationId;
    session.nominatedPlayerFantacalcioId = fantacalcioId; session.nominatingSeatId = seatId;
    session.currentHighestBid = null; session.highestBidderSeatId = null; session.bidSeq = 0; session.deadlineAt = deadlineAt;

    this._persist();
    return { ok:true, conflict:false, revision:nextRevision, nominationId, nominationSeq:nextSeq, openingPrice:rules.openingPrice, deadlineAt, error:null };
  }

  async placeBid(auctionId, nominationId, seatId, amount, expectedBidSeq, commandId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    const fail = (msg, extra) => Object.assign({ ok:false, conflict:false, bidSeq:null, amount:null, deadlineAt:null, revision:null, error:msg }, extra||{});
    if(!a) return fail("Asta demo non trovata.");
    if(expectedBidSeq === null || expectedBidSeq === undefined){
      return fail("expectedBidSeq è obbligatorio (nessun bypass del controllo di concorrenza).");
    }
    if(!commandId) return fail("commandId è obbligatorio.");
    if(!isValidUuid(commandId)) return fail("commandId deve essere un UUID valido (parità col tipo SQL `uuid` di 0014).");
    // La revoca di accesso prevale sempre sul replay: verificata PRIMA del lookup dell'idempotenza,
    // stesso ordine di place_bid() lato reale (0014).
    if(!membershipOf(a, userId)) return fail("Non sei più membro di questa asta.");

    const bids = bidsOf(a);
    a.bids = bids;
    const existingCmd = bids.find(b => b.commandId === commandId);
    if(existingCmd){
      if(existingCmd.nominationId === nominationId && existingCmd.seatId === seatId &&
         existingCmd.amount === amount && existingCmd.submittedByUserId === userId){
        return { ok:true, conflict:false, bidSeq:existingCmd.bidSeq, amount:existingCmd.amount,
          deadlineAt:existingCmd.deadlineAtAfterBid, revision:existingCmd.resultingRevision, error:null };
      }
      return fail("commandId già usato con un payload o un chiamante diverso.");
    }

    const seat = seatsOf(a).find(s => s.id === seatId);
    if(!seat || seat.assignedUserId !== userId) return fail("Questa squadra non è controllata da te.");

    const session = liveSessionOf(a);
    if(!session) return fail("Sessione live non trovata.");
    if(session.currentNominationId !== nominationId) return fail("Questa nomina non è più quella corrente.");
    if(session.nominationStatus !== "open") return fail("Nessuna nomina aperta al momento.");
    if(expectedBidSeq !== session.bidSeq){
      return fail("Un'altra offerta è già stata registrata nel frattempo.",
        { conflict:true, bidSeq:session.bidSeq, amount:session.currentHighestBid, deadlineAt:session.deadlineAt, revision:session.revision });
    }

    const nomination = nominationsOf(a).find(n => n.id === nominationId);
    if(!nomination || nomination.status !== "open") return fail("Invariante violata: la nomina corrente non risulta open.");
    const rules = liveRulesOf(a);
    if(!rules) return fail("Invariante violata: regole live assenti per una nomina già aperta.");

    const decisionTime = this._clock();
    if(decisionTime.getTime() >= new Date(session.deadlineAt).getTime()) return fail("La nomina è scaduta.");
    // DEMO PARITY (round alpha.5-r4-r2, audit indipendente): amount è tipizzato `integer` lato SQL
    // (0014) — stessa motivazione del vincolo analogo in configureLiveRules() sopra.
    if(!(Number.isInteger(amount) && amount > 0)) return fail("Importo non valido (deve essere un numero intero positivo).");
    if(session.highestBidderSeatId === seatId) return fail("Sei già tu il miglior offerente, non puoi rilanciare su te stesso.");
    if(session.currentHighestBid === null){
      if(amount < nomination.openingPrice) return fail("L'importo deve essere almeno pari al prezzo di apertura.");
    } else {
      if(amount <= session.currentHighestBid) return fail("L'importo deve superare l'offerta corrente.");
    }

    // Round alpha.5-r5-r1: budget/capacity/duplicate — parità con place_bid() lato reale (0015).
    // auction_purchases (purchasesOf) è l'UNICA authority economica, mai un campo mutabile di
    // budget/roster residuo. Nessun hard reserve per slot futuri.
    const rosterRules = rosterRulesOf(a);
    if(!rosterRules) return fail("Invariante violata: regole di rosa/budget assenti per una nomina già aperta.");

    const purchases = purchasesOf(a);
    const spent = purchases.filter(p => p.seatId === seatId).reduce((sum,p)=>sum+p.amount, 0);
    const remaining = rosterRules.initialBudgetPerSeat - spent;
    if(amount > remaining) return fail("Budget residuo insufficiente per questa offerta (residuo: " + remaining + ", offerta: " + amount + ").");

    const roleCount = purchases.filter(p => p.seatId === seatId && p.roleAtPurchase === nomination.playerRoleAtNomination).length;
    const roleCapacity = { POR: rosterRules.slotPor, DIF: rosterRules.slotDif, CEN: rosterRules.slotCen, ATT: rosterRules.slotAtt }[nomination.playerRoleAtNomination];
    if(roleCount >= roleCapacity){
      return fail("Nessuno slot disponibile per il ruolo " + nomination.playerRoleAtNomination + " in questa squadra (" + roleCount + " / " + roleCapacity + ").");
    }

    if(purchases.some(p => p.playerFantacalcioId === nomination.playerFantacalcioId)){
      return fail("Invariante violata: il player di questa nomina risulta già presente nel ledger acquisti.");
    }

    const newBidSeq = session.bidSeq + 1;
    const nextRevision = session.revision + 1;
    const newDeadline = rules.resetOnBid
      ? new Date(decisionTime.getTime() + rules.resetTimerSeconds * 1000).toISOString()
      : session.deadlineAt;

    session.revision = nextRevision; session.currentHighestBid = amount; session.highestBidderSeatId = seatId;
    session.bidSeq = newBidSeq; session.deadlineAt = newDeadline;

    bids.push({
      id: uid("bid"), nominationId, seatId, amount, bidSeq:newBidSeq, submittedByUserId:userId,
      deadlineAtAfterBid:newDeadline, resultingRevision:nextRevision, commandId, createdAt: nowISO()
    });

    this._persist();
    return { ok:true, conflict:false, bidSeq:newBidSeq, amount, deadlineAt:newDeadline, revision:nextRevision, error:null };
  }

  async finalizeIfExpired(auctionId, nominationId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    const fail = (msg, extra) => Object.assign({ ok:false, alreadyFinalized:false, nominationId:null, outcome:null, winningSeatId:null, winningAmount:null, revision:null, error:msg }, extra||{});
    if(!a) return fail("Asta demo non trovata.");
    if(!membershipOf(a, userId)) return fail("Non sei owner né membro di questa asta.");

    const nomination = nominationsOf(a).find(n => n.id === nominationId);
    if(!nomination) return fail("Nomina non trovata per questa asta.");

    if(nomination.status !== "open"){
      return { ok:true, alreadyFinalized:true, nominationId:nomination.id, outcome:nomination.status,
        winningSeatId:nomination.finalSeatId, winningAmount:nomination.finalAmount, revision:nomination.closedAtRevision, error:null };
    }

    const session = liveSessionOf(a);
    if(!session) return fail("Invariante violata: sessione live assente.");
    if(session.nominationStatus !== "open" || session.currentNominationId !== nomination.id){
      return fail("Invariante violata: stato live e nomina non coerenti.");
    }

    const decisionTime = this._clock();
    if(decisionTime.getTime() < new Date(session.deadlineAt).getTime()) return fail("La nomina non è ancora scaduta.");

    const nextRevision = session.revision + 1;
    let outcome;
    if(session.currentHighestBid !== null){
      outcome = "pending_settlement";
      nomination.status = "pending_settlement"; nomination.finalAmount = session.currentHighestBid;
      nomination.finalSeatId = session.highestBidderSeatId; nomination.closedAt = nowISO(); nomination.closedAtRevision = nextRevision;
      session.revision = nextRevision; session.nominationStatus = "pending_settlement";
    } else {
      outcome = "unsold";
      nomination.status = "unsold"; nomination.finalAmount = null; nomination.finalSeatId = null;
      nomination.closedAt = nowISO(); nomination.closedAtRevision = nextRevision;
      session.revision = nextRevision; session.nominationStatus = null; session.currentNominationId = null;
      session.nominatedPlayerFantacalcioId = null; session.nominatingSeatId = null;
      session.currentHighestBid = null; session.highestBidderSeatId = null; session.deadlineAt = null; session.bidSeq = 0;
    }

    this._persist();
    return {
      ok:true, alreadyFinalized:false, nominationId:nomination.id, outcome,
      winningSeatId: outcome==="pending_settlement" ? nomination.finalSeatId : null,
      winningAmount: outcome==="pending_settlement" ? nomination.finalAmount : null,
      revision: nextRevision, error:null
    };
  }

  // ---------------- Live auction: settlement / purchase ledger / budget / roster (round alpha.5-r5-r1) ----------------
  // Parità di comportamento osservabile con SupabaseBackendAdapter/0015+0020: stessa policy
  // server-written per prezzoMinimo (sempre 1, mai un parametro del chiamante); slotPor/Dif/Cen/Att
  // sono invece configurabili dal round 2.1.0 (default 3/8/8/6 se omessi — vedi configureRosterRules()
  // sotto), stesso ordine "autorizzazione -> replay -> revalidation obbligatoria" di settle_nomination(), stesso
  // this._clock() NON usato qui (settle non calcola scadenze). auction_purchases (purchasesOf) resta
  // l'UNICA authority economica anche lato demo: nessun remaining_budget/roster_count persistito.

  async loadRosterRules(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return null;
    if(!membershipOf(a, userId)) return null;
    const rules = rosterRulesOf(a);
    if(!rules) return null;
    return {
      auctionId, initialBudgetPerSeat: rules.initialBudgetPerSeat, prezzoMinimo: rules.prezzoMinimo,
      slotPor: rules.slotPor, slotDif: rules.slotDif, slotCen: rules.slotCen, slotAtt: rules.slotAtt, revision: rules.revision
    };
  }

  async configureRosterRules(auctionId, initialBudgetPerSeat, expectedRevision, slotPOR = 3, slotDIF = 8, slotCEN = 8, slotATT = 6){
    const { a, error } = this._requireOwnerForLiveSession(auctionId);
    if(!a) return { ok:false, conflict:false, revision:null, error };
    if(expectedRevision === null || expectedRevision === undefined){
      return { ok:false, conflict:false, revision:null, error:"expectedRevision è obbligatorio (nessun bypass del controllo di concorrenza)." };
    }
    // DEMO PARITY: initial_budget_per_seat è tipizzato `integer` lato SQL (0015) con CHECK 50..5000 —
    // stesso vincolo qui, mai lasciato scoprire solo da una connessione reale.
    if(!(Number.isInteger(initialBudgetPerSeat) && initialBudgetPerSeat >= 50 && initialBudgetPerSeat <= 5000)){
      return { ok:false, conflict:false, revision:null, error:"Il budget iniziale deve essere un numero intero compreso fra 50 e 5000." };
    }
    // DEMO PARITY (round 2.1.0): stessa validazione esplicita di 0020 per le taglie di rosa per ruolo —
    // mai affidata solo al CHECK >=0 di tabella. Zero per un singolo ruolo è ammesso; la somma deve
    // essere >0 e <= initialBudgetPerSeat (prezzoMinimo resta fisso a 1, quindi una rosa più grande
    // del budget sarebbe irraggiungibile anche al prezzo minimo).
    if(![slotPOR, slotDIF, slotCEN, slotATT].every(v => Number.isInteger(v) && v >= 0)){
      return { ok:false, conflict:false, revision:null, error:"Ciascuna taglia di rosa per ruolo deve essere un intero >= 0." };
    }
    const totalSlots = slotPOR + slotDIF + slotCEN + slotATT;
    if(totalSlots <= 0){
      return { ok:false, conflict:false, revision:null, error:"La somma delle taglie di rosa per ruolo deve essere maggiore di zero." };
    }
    if(totalSlots > initialBudgetPerSeat){
      return { ok:false, conflict:false, revision:null, error:"La somma delle taglie di rosa (" + totalSlots + ") non può superare il budget iniziale (" + initialBudgetPerSeat + "): irraggiungibile anche al prezzo minimo." };
    }

    const session = liveSessionOf(a);
    if(session && session.lifecycle !== "setup"){
      return { ok:false, conflict:false, revision:null, error:"Non modificabile fuori da setup (stato attuale: " + session.lifecycle + ")." };
    }

    const existing = rosterRulesOf(a);
    if(!existing){
      if(expectedRevision !== 0){
        return { ok:false, conflict:true, revision:0, error:"Un'altra scheda ha già inizializzato le regole di rosa/budget." };
      }
      a.rosterRules = { initialBudgetPerSeat, prezzoMinimo:1, slotPor:slotPOR, slotDif:slotDIF, slotCen:slotCEN, slotAtt:slotATT, revision:1, updatedAt: nowISO() };
      this._persist();
      return { ok:true, conflict:false, revision:1, error:null };
    }
    if(expectedRevision !== existing.revision){
      return { ok:false, conflict:true, revision:existing.revision, error:"Un'altra scheda ha già modificato le regole di rosa/budget." };
    }
    // prezzoMinimo resta SEMPRE fisso a 1 (policy applicativa, mai un parametro del chiamante); le
    // taglie di rosa (round 2.1.0) sono invece quelle passate/validate sopra — parità con
    // configure_roster_rules() lato reale (0020).
    existing.initialBudgetPerSeat = initialBudgetPerSeat;
    existing.prezzoMinimo = 1; existing.slotPor = slotPOR; existing.slotDif = slotDIF; existing.slotCen = slotCEN; existing.slotAtt = slotATT;
    existing.revision += 1; existing.updatedAt = nowISO();
    this._persist();
    return { ok:true, conflict:false, revision:existing.revision, error:null };
  }

  async listAuctionPurchases(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return [];
    if(!membershipOf(a, userId)) return [];
    return purchasesOf(a).slice().sort((x,y)=>x.purchaseSeq-y.purchaseSeq).map(p => ({
      id: p.id, nominationId: p.nominationId, seatId: p.seatId, playerFantacalcioId: p.playerFantacalcioId,
      amount: p.amount, roleAtPurchase: p.roleAtPurchase, purchaseSeq: p.purchaseSeq,
      resultingRevision: p.resultingRevision, settledByUserId: p.settledByUserId, createdAt: p.createdAt
    }));
  }

  // listAuctionNominations — round A7-P1-6: stessa parità già stabilita per listAuctionPurchases()
  // sopra — riusa VERBATIM l'array nominationsOf(a) già popolato da nominatePlayer()/
  // finalizeIfExpired()/settleNomination() (nessuna seconda struttura dati inventata qui), ordinato
  // per nominationSeq crescente. Proiezione minima di campi, mai commandId/playerRoleAtNomination/
  // deadlineAt — solo quanto richiesto dal merge puro nomination_opened/player_sold/player_unsold.
  async listAuctionNominations(auctionId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return [];
    if(!membershipOf(a, userId)) return [];
    return nominationsOf(a).slice().sort((x,y)=>x.nominationSeq-y.nominationSeq).map(n => ({
      id: n.id, nominationSeq: n.nominationSeq, playerFantacalcioId: n.playerFantacalcioId,
      nominatingSeatId: n.nominatingSeatId, openingPrice: n.openingPrice, status: n.status,
      finalAmount: n.finalAmount, finalSeatId: n.finalSeatId,
      openedAt: n.openedAt, openedAtRevision: n.openedAtRevision,
      closedAt: n.closedAt, closedAtRevision: n.closedAtRevision
    }));
  }

  // listBidsForNomination — round A7-P1-5: stessa parità già stabilita per listAuctionPurchases()
  // sopra — riusa VERBATIM l'array bidsOf(a) già popolato da placeBid() (nessuna seconda struttura
  // dati inventata qui), filtrato per nominationId, ordinato per bidSeq crescente. Proiezione minima
  // di campi, mai submittedByUserId/commandId (§25 del design, mai esposti alla UI).
  async listBidsForNomination(auctionId, nominationId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    if(!a) return [];
    if(!membershipOf(a, userId)) return [];
    return bidsOf(a).filter(b => b.nominationId === nominationId).slice().sort((x,y)=>x.bidSeq-y.bidSeq).map(b => ({
      id: b.id, auctionId, nominationId: b.nominationId, seatId: b.seatId, amount: b.amount, bidSeq: b.bidSeq, createdAt: b.createdAt
    }));
  }

  async settleNomination(auctionId, nominationId){
    const userId = this._store.sessionUserId;
    const a = this._store.auctions.find(x => x.id === auctionId);
    const fail = (msg, extra) => Object.assign({ ok:false, alreadySettled:false, purchaseId:null, seatId:null, playerFantacalcioId:null, amount:null, revision:null, error:msg }, extra||{});
    if(!a) return fail("Asta demo non trovata.");
    // La revoca di accesso prevale sempre sul replay: verificata PRIMA di leggere la nomina — un
    // membro rimosso riceve un rifiuto esplicito anche su una nomina già settled (parità con
    // settle_nomination() lato reale: il lock sul member precede sempre la lettura della nomina).
    if(!membershipOf(a, userId)) return fail("Non sei owner né membro di questa asta.");

    const purchases = purchasesOf(a);
    a.purchases = purchases;
    const nomination = nominationsOf(a).find(n => n.id === nominationId);
    if(!nomination) return fail("Nomina non trovata per questa asta.");

    if(nomination.status === "settled"){
      const purchase = purchases.find(p => p.nominationId === nominationId);
      if(!purchase) return fail("Invariante violata: nomina settled senza una purchase corrispondente.");
      return { ok:true, alreadySettled:true, purchaseId:purchase.id, seatId:purchase.seatId,
        playerFantacalcioId:purchase.playerFantacalcioId, amount:purchase.amount, revision:purchase.resultingRevision, error:null };
    }
    if(nomination.status !== "pending_settlement"){
      return fail("La nomina non è in attesa di finalizzazione (stato attuale: " + nomination.status + ").");
    }

    const session = liveSessionOf(a);
    if(!session) return fail("Invariante violata: sessione live assente.");
    if(session.nominationStatus !== "pending_settlement" || session.currentNominationId !== nomination.id
       || session.highestBidderSeatId !== nomination.finalSeatId || session.currentHighestBid !== nomination.finalAmount){
      return fail("Invariante violata: stato live e nomina non coerenti.");
    }

    // Revalidation OBBLIGATORIA (budget, capacity per ruolo, duplicate-player) — SEMPRE tutte e tre,
    // usando final_amount/final_seat_id/player_role_at_nomination + il ledger corrente + le regole
    // persistite. Un fallimento qui è un errore DEDICATO ("richiede revisione amministrativa..."), mai
    // un normale rifiuto di bid: zero purchase inserite, nomina ancora pending_settlement.
    const rules = rosterRulesOf(a);
    if(!rules){
      return fail("Richiede revisione amministrativa — nomina pre-esistente non compatibile con le regole economiche attuali (nessuna regola di rosa/budget configurata per questa asta).");
    }
    if(purchases.some(p => p.playerFantacalcioId === nomination.playerFantacalcioId)){
      return fail("Richiede revisione amministrativa — player già presente nel ledger acquisti di questa asta, nomina pre-esistente non compatibile con lo stato corrente del ledger.");
    }
    const spent = purchases.filter(p => p.seatId === nomination.finalSeatId).reduce((sum,p)=>sum+p.amount, 0);
    const remaining = rules.initialBudgetPerSeat - spent;
    if(nomination.finalAmount > remaining){
      return fail("Richiede revisione amministrativa — nomina pre-esistente non compatibile con le regole economiche attuali (budget residuo insufficiente per questa squadra).");
    }
    const roleCount = purchases.filter(p => p.seatId === nomination.finalSeatId && p.roleAtPurchase === nomination.playerRoleAtNomination).length;
    const roleCapacity = { POR: rules.slotPor, DIF: rules.slotDif, CEN: rules.slotCen, ATT: rules.slotAtt }[nomination.playerRoleAtNomination];
    if(roleCount >= roleCapacity){
      return fail("Richiede revisione amministrativa — nomina pre-esistente non compatibile con le regole economiche attuali (nessuno slot disponibile per il ruolo " + nomination.playerRoleAtNomination + ").");
    }

    const nextSeq = purchases.reduce((max,p)=>Math.max(max, p.purchaseSeq), 0) + 1;
    const nextRevision = session.revision + 1;
    const purchaseId = uid("purchase");

    purchases.push({
      id: purchaseId, nominationId: nomination.id, seatId: nomination.finalSeatId,
      playerFantacalcioId: nomination.playerFantacalcioId, amount: nomination.finalAmount,
      roleAtPurchase: nomination.playerRoleAtNomination, purchaseSeq: nextSeq, resultingRevision: nextRevision,
      settledByUserId: userId, createdAt: nowISO()
    });

    nomination.status = "settled"; nomination.settledAt = nowISO(); nomination.settledAtRevision = nextRevision;

    session.revision = nextRevision; session.nominationStatus = null; session.currentNominationId = null;
    session.nominatedPlayerFantacalcioId = null; session.nominatingSeatId = null;
    session.currentHighestBid = null; session.highestBidderSeatId = null; session.deadlineAt = null; session.bidSeq = 0;

    this._persist();
    return { ok:true, alreadySettled:false, purchaseId, seatId:nomination.finalSeatId,
      playerFantacalcioId:nomination.playerFantacalcioId, amount:nomination.finalAmount, revision:nextRevision, error:null };
  }
}
